import { memo, useMemo, useState } from "react";
import type { IncidentFeature, IncidentAuditEntry } from "@kmitl/shared";
import { IncidentReplyForm } from "./IncidentReplyForm";
import { IncidentAuditThread } from "./IncidentAuditThread";

/**
 * IncidentCard — issue-card UI for a clicked city-report dot.
 *
 * What it does that Traffy can't:
 *   • Names the owner from the start (staffName + teamName, single row),
 *     so the "musical chairs of responsibility erosion" doesn't have
 *     anywhere to start.
 *   • Renders the audit_log as a categorized timeline. Each staff_message
 *     carries a self-declared kind: acknowledge / request / inform /
 *     completed / blocked — color-coded so an admin scanning the thread
 *     sees the conversational shape in one glance.
 *   • Two views:
 *        INTERNAL  — full thread between admin + staff (default for ops)
 *        CITIZEN   — filtered to status transitions + replies that were
 *                    actually pushed to the citizen's LINE, so the
 *                    operator can verify what the reporter has seen.
 *   • Magic Eye AI summary surfaces above the description so the
 *     classification is auditable.
 *   • Before/after photos side-by-side once solutionImageUrl is set.
 *
 * No reply UI yet — that needs the bot to expose a POST endpoint for
 * dashboard-originated admin messages with an API key. Tracked as the
 * next item; for now this card is read-only.
 */

interface Props {
  incident: IncidentFeature | null;
  onClose: () => void;
  /** API base URL — same as the rest of the app pulls from. */
  apiBase: string;
  /** Called after a successful reply so the parent can refetch. */
  onReplied?: () => void;
}

type View = "internal" | "citizen";

/** Kinds the bot accepts on /api/reports/:id/message. */
const REPLY_KINDS: Array<{ id: string; label: string; color: string }> = [
  { id: "acknowledge",  label: "ACK",       color: "var(--data)" },
  { id: "request_info", label: "REQUEST",   color: "var(--accent)" },
  { id: "progress",     label: "PROGRESS",  color: "var(--warn)" },
  { id: "completed",    label: "COMPLETED", color: "var(--good)" },
  { id: "note",         label: "NOTE",      color: "var(--text-2)" },
];

const STATUS_COLOR: Record<string, string> = {
  received:      "var(--text-3)",
  assigned:      "var(--data)",
  "in-progress": "var(--warn)",
  resolved:      "var(--good)",
};

const STATUS_LABEL: Record<string, string> = {
  received:      "RECEIVED",
  assigned:      "ASSIGNED",
  "in-progress": "IN PROGRESS",
  resolved:      "RESOLVED",
};

const SEVERITY_COLOR: Record<string, string> = {
  low:      "var(--text-3)",
  medium:   "var(--data)",
  high:     "var(--warn)",
  critical: "var(--bad)",
};

function fmtAgo(iso: string | undefined): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const min = Math.round(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  if (min < 60 * 24) return `${Math.round(min / 60)}h ago`;
  return `${Math.round(min / (60 * 24))}d ago`;
}

function isCitizenVisible(entry: IncidentAuditEntry): boolean {
  // Status changes are always visible.
  if (entry.type === "status_change") return true;
  // Staff messages are only visible if delivered to LINE.
  if (entry.type === "staff_message") return entry.delivered === true;
  // Citizen replies — also surfaced.
  if (entry.type === "citizen_reply") return true;
  // AI findings + category locks stay internal.
  return false;
}

function IncidentCardImpl({ incident, onClose, apiBase, onReplied }: Props) {
  const [view, setView] = useState<View>("internal");
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyKind, setReplyKind] = useState<string>("acknowledge");
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  async function sendReply() {
    if (!incident) return;
    const text = replyText.trim();
    if (!text) {
      setReplyError("Message can't be empty");
      return;
    }
    setReplyBusy(true);
    setReplyError(null);
    try {
      const res = await fetch(`${apiBase}/api/incidents/city-reports/${encodeURIComponent(incident.id)}/reply`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          text,
          kind: replyKind,
          staffName: "KMITL Admin",
        }),
      });
      const json = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string; delivered?: boolean };
      if (!res.ok || !json.success) {
        setReplyError(json.error ?? `Send failed (${res.status})`);
      } else {
        setReplyText("");
        setReplyOpen(false);
        onReplied?.();
      }
    } catch (err) {
      setReplyError(String((err as Error).message ?? err));
    } finally {
      setReplyBusy(false);
    }
  }

  // Hooks must run unconditionally — compute thread + status timeline
  // even when there's no incident, then bail in the render.
  const audit = useMemo(() => incident?.auditLog ?? [], [incident]);
  const visibleEntries = useMemo(
    () => (view === "internal" ? audit : audit.filter(isCitizenVisible)),
    [audit, view],
  );

  const statusTimeline = useMemo(() => {
    const order: IncidentFeature["status"][] = ["received", "assigned", "in-progress", "resolved"];
    const reached = new Set<string>(["received"]);
    for (const e of audit) {
      if (e.type === "status_change" && e.to) reached.add(e.to);
    }
    if (incident?.status) reached.add(incident.status);
    return order.map((s) => ({ status: s, reached: reached.has(s) }));
  }, [incident, audit]);

  if (!incident) return null;

  const severityColor = SEVERITY_COLOR[incident.severity] ?? "var(--text-2)";
  const statusColor = STATUS_COLOR[incident.status] ?? "var(--text-3)";

  return (
    <aside
      className="incident-card"
      role="dialog"
      aria-label={`Incident ${incident.ticketNumber ?? incident.id}`}
    >
      <header className="incident-card-head">
        <div className="incident-card-id">
          <span className="eyebrow mono">CR · {incident.ticketNumber ?? incident.id.slice(-6)}</span>
          <h3 className="incident-card-title">{incident.title}</h3>
        </div>
        <button
          onClick={onClose}
          className="incident-card-close mono"
          aria-label="Close incident card"
          title="Close (Esc)"
        >
          [ESC]
        </button>
      </header>

      {/* Pills row: status · severity · category · reported-ago */}
      <div className="incident-pills">
        <span className="incident-pill mono" style={{ borderColor: statusColor, color: statusColor }}>
          {STATUS_LABEL[incident.status] ?? incident.status.toUpperCase()}
        </span>
        <span className="incident-pill mono" style={{ borderColor: severityColor, color: severityColor }}>
          {incident.severity.toUpperCase()}
        </span>
        <span className="incident-pill mono">
          {(incident.categoryLocked || incident.category).toUpperCase()}
        </span>
        <span className="incident-pill incident-pill-ghost mono">
          {fmtAgo(incident.reportedAt)}
        </span>
      </div>

      {/* Status timeline — no musical chairs */}
      <ol className="incident-timeline" aria-label="Status timeline">
        {statusTimeline.map(({ status, reached }, i) => (
          <li
            key={status}
            className={`incident-tl-node ${reached ? "reached" : "pending"} ${incident.status === status ? "current" : ""}`}
          >
            <span className="incident-tl-dot" style={{ background: reached ? STATUS_COLOR[status] : "var(--line)" }} />
            <span className="incident-tl-label mono">{STATUS_LABEL[status]}</span>
            {i < statusTimeline.length - 1 && (
              <span
                className="incident-tl-line"
                style={{ background: statusTimeline[i + 1].reached ? STATUS_COLOR[statusTimeline[i + 1].status] : "var(--line)" }}
              />
            )}
          </li>
        ))}
      </ol>

      {/* AI verdict + photos */}
      {incident.aiSummary && (
        <div className="incident-ai">
          <span className="eyebrow mono">Magic Eye · AI verdict</span>
          <p className="incident-ai-body">{incident.aiSummary}</p>
        </div>
      )}
      {(incident.imageUrl || incident.solutionImageUrl) && (
        <div className="incident-photos">
          {incident.imageUrl && (
            <figure className="incident-photo">
              <img src={incident.imageUrl} alt={`Citizen photo for ${incident.title}`} loading="lazy" />
              <figcaption className="caption mono">BEFORE · cited photo</figcaption>
            </figure>
          )}
          {incident.solutionImageUrl && (
            <figure className="incident-photo">
              <img src={incident.solutionImageUrl} alt="Resolution photo from staff" loading="lazy" />
              <figcaption className="caption mono" style={{ color: "var(--good)" }}>AFTER · staff resolution</figcaption>
            </figure>
          )}
        </div>
      )}

      {/* Owner — named, single row, no committee */}
      <div className="incident-owner">
        <div className="incident-owner-row">
          <span className="eyebrow mono">Owner</span>
          <span className="incident-owner-name">
            {incident.staffName ? incident.staffName : <span style={{ color: "var(--text-3)" }}>unassigned</span>}
            {incident.teamName && <span className="incident-owner-team mono"> · {incident.teamName}</span>}
          </span>
        </div>
        {incident.staffComment && (
          <p className="incident-owner-comment">{incident.staffComment}</p>
        )}
      </div>

      {/* Description from citizen */}
      {incident.description && (
        <div className="incident-desc">
          <span className="eyebrow mono">Citizen description</span>
          <p>{incident.description}</p>
          {incident.nickname && (
            <span className="caption mono incident-reporter">— {incident.nickname}</span>
          )}
        </div>
      )}

      {/* Internal/Citizen view toggle */}
      <div className="incident-tabs" role="tablist" aria-label="Thread view">
        <button
          role="tab"
          aria-selected={view === "internal"}
          className={`incident-tab mono ${view === "internal" ? "active" : ""}`}
          onClick={() => setView("internal")}
        >
          INTERNAL · {audit.length}
        </button>
        <button
          role="tab"
          aria-selected={view === "citizen"}
          className={`incident-tab mono ${view === "citizen" ? "active" : ""}`}
          onClick={() => setView("citizen")}
        >
          CITIZEN VIEW · {audit.filter(isCitizenVisible).length}
        </button>
      </div>

      <IncidentAuditThread
        entries={visibleEntries}
        emptyMessage={
          view === "internal"
            ? "No internal messages yet. Once staff acknowledges or updates the ticket, the conversation lands here."
            : "Citizen has not yet seen any update beyond the initial receipt. Once an admin reply lands on LINE, it appears here."
        }
      />

      <footer className="incident-card-foot">
        <span className="caption mono incident-foot-meta">
          {incident.reporterPlatform === "city-reporter" ? "City Reporter · " : ""}
          tenant {incident.tenantId ?? "(unscoped)"}
        </span>
        {!replyOpen && (
          <button
            className="mono incident-reply-btn"
            onClick={() => setReplyOpen(true)}
            title="Send a categorized message to the citizen on LINE — recorded in audit log"
          >
            REPLY TO CITIZEN ↗
          </button>
        )}
      </footer>

      {replyOpen && (
        <IncidentReplyForm
          kinds={REPLY_KINDS}
          replyKind={replyKind}
          replyText={replyText}
          replyBusy={replyBusy}
          replyError={replyError}
          onKindChange={setReplyKind}
          onTextChange={setReplyText}
          onSend={sendReply}
          onCancel={() => { setReplyOpen(false); setReplyError(null); }}
        />
      )}
    </aside>
  );
}

export const IncidentCard = memo(IncidentCardImpl);
